import { Collapse, Icon } from "@blueprintjs/core"
import CustomDialog from "components/Atoms/CustomDialog"
import CustomTag from "components/Atoms/CustomTag"
import CustomTooltip from "components/Atoms/CustomTooltip"
import StringInput from "components/Atoms/StringInput"
import ButtonMutation from "components/Molecules/ButtonMutation"
import FormController from "components/Molecules/FormController"
import OrderDetailDialog from "components/Templates/order/OrderDetailDialog"
import { useCurrentDepartmentAccess } from "graphql/auth/auth.hooks"
import { UPDATE_BOOKING } from "graphql/trip/trip.mutations"
import { useAuthorizationModules } from "hooks/useAuth"
import { set } from "lodash-es"
import React, { useCallback, useEffect, useState } from "react"
import { ErrorToast, SuccessToast } from "utils/AppToaster"
import classNames from "utils/classNames"
import Format from "utils/Format"
import BookingOrderItems from "./BookingOrderItems"
function BookingCard({ booking, index }) {
  const isEcom = useCurrentDepartmentAccess()

  const disabled = useAuthorizationModules("edit", "booking")
  const sch = booking?.schedule
  const plot = sch?.plot
  // If the given booking is an order (Front end trip) then get the trackingCode or the gatePass
  const tag = booking?.trackingCode ?? sch?.gatePass
  // If the booking is an order get the user or else get the farmer
  const user = booking?.user ?? plot?.farmer
  const address = booking?.shippingAddress ?? plot?.address

  const status = booking?.orderStatusObj

  const [isExpanded, setExpanded] = useState(true)
  const [isOrderDetailOpen, setOrderDetail] = useState(false)
  const [isOpen, setOpen] = useState(false)
  const [bookingsState, setBookings] = useState(
    isEcom ? booking?.bookingsByTrip : [booking]
  )
  const handleToggle = useCallback(() => {
    setExpanded(prevExpanded => !prevExpanded)
  }, [setExpanded])

  useEffect(() => {
    if (isEcom) {
      setBookings(booking?.bookingsByTrip)
    } else {
      setBookings([booking])
    }
  }, [booking, isEcom])
  const procured = booking?.bookingsByTrip
    ? booking?.bookingsByTrip?.reduce((p, i) => p + +i.procuredQuantity, 0)
    : booking?.procuredQuantity
  const required = booking?.bookingsByTrip
    ? booking?.bookingsByTrip?.reduce((p, i) => p + +i.requiredQuantity, 0)
    : booking?.requiredQuantity

  function handleChange(path, value) {
    const bookingsStateCopy = [...bookingsState]
    set(bookingsStateCopy, path, value)
    setBookings(bookingsStateCopy)
  }

  function handleClick() {
    setOrderDetail(prev => !prev)
  }

  return (
    <div
      className={classNames("vt-booking mb-2", {
        "vt-booking-pending": required - procured > 0,
        "vt-order-completed": /CMP/.test(status?.id),
        "vt-order-partial-reject": /PRJ/.test(status?.id),
        "vt-order-cancelled": /CBV|CBU|FRJ/.test(status?.id),
      })}
    >
      <CustomTag
        interactive={isEcom}
        onClick={isEcom ? handleClick : null}
        round
        minimal
      >
        {tag}
      </CustomTag>
      {isEcom && (
        <CustomTag className="ml-2" round minimal intent={status?.intent}>
          {status?.name}
        </CustomTag>
      )}
      <div
        className="vt-booking-tile vt-tile-header grid grid-cols-12 gap-4 px-2"
        onClick={handleToggle}
      >
        <div className="col-span-1">
          <Icon icon="user" />
        </div>
        <div className="vt-tile-content col-span-9">
          <span className="vt-card-title">
            {index + 1}. {user?.fullName}
          </span>
          <span className="vt-card-subtitle">
            {address?.village}, {address?.taluka}
          </span>
        </div>
        <div className="col-span-1">
          <Icon icon={isExpanded ? "chevron-up" : "chevron-down"} />
        </div>
      </div>
      <Collapse isOpen={isExpanded}>
        <div className="vt-booking-tile px-2">
          <div className="col-span-1">
            <Icon icon="box" color="$vt-gray-600" />
          </div>
          <div className="vt-tile-content  col-span-10">
            <span className="vt-card-title">
              Payload{" "}
              <CustomTooltip content="Edit Quantity">
                <CustomTag
                  intent={status?.intent ?? "success"}
                  interactive
                  className="ml-2"
                  minimal
                  small
                  round
                  onClick={() => setOpen(true)}
                >
                  {procured ?? 0} / {required ?? 0}
                </CustomTag>
              </CustomTooltip>
            </span>
            {booking?.bookingsByTrip ? (
              <BookingOrderItems
                status={status}
                bookings={booking?.bookingsByTrip}
              />
            ) : (
              <span className="vt-card-subtitle mt-1">
                {sch?.variety?.commodity?.name} - ({sch?.variety?.name})
              </span>
            )}
          </div>
        </div>
      </Collapse>
      <CustomDialog
        light
        width="40%"
        icon="box"
        title={
          <span>
            Update bookings for order - <span className="vt-muted">{tag}</span>
          </span>
        }
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        footerRightButton={
          <ButtonMutation
            disabled={disabled}
            mutation={UPDATE_BOOKING}
            onError={err => {
              err.graphQLErrors.forEach(err => {
                ErrorToast(err.message)
              })
            }}
            onCompleted={data => {
              SuccessToast(`Bookings updated successful`)
              setOpen(false)
            }}
            variables={{
              updateBookings: bookingsState?.map(b => ({
                bookingId: b?.id,
                deliveredQuantity: b?.procuredQuantity,
              })),
            }}
            icon="confirm"
            text="Submit"
          />
        }
      >
        <FormController onChange={handleChange} source={bookingsState}>
          {bookingsState?.map((b, idx) => {
            const variety = b?.schedule?.variety

            return (
              <div key={b?.id} className="vt-edit-booking-row">
                <div>
                  {isEcom
                    ? b?.orderItem?.sellableItem?.name
                    : Format.variety(variety)}
                </div>
                <div className="ml-3">
                  <StringInput
                    disabled={disabled}
                    className="mb-0"
                    big={false}
                    path={`${idx}.procuredQuantity`}
                  />{" "}
                  <span className="mx-2">/</span>
                  <span>{b.requiredQuantity}</span>
                </div>
              </div>
            )
          })}
        </FormController>
      </CustomDialog>
      {isEcom && (
        <OrderDetailDialog
          isOpen={isOrderDetailOpen}
          onClose={() => setOrderDetail(null)}
          orderId={booking?.id}
        />
      )}
    </div>
  )
}
export default BookingCard
